// ===== color-utils.js =====

/** @typedef {{r:number,g:number,b:number,a:number}} RGBA */
/** @typedef {{h:number,s:number,l:number,a:number}} HSLA */

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));
const fract = (n: number) => n - Math.floor(n);

/** Parse #RGB/#RRGGBB/#RGBA/#RRGGBBAA, rgb/rgba(), hsl/hsla() into RGBA */
export function parseColor(input: string) {
  const s = String(input).trim().toLowerCase();

  // HEX
  if (s[0] === '#') {
    const hex = s.slice(1);
    const expand = (h: string) => (h.length === 1 ? h + h : h);
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(expand(hex[0]), 16);
      const g = parseInt(expand(hex[1]), 16);
      const b = parseInt(expand(hex[2]), 16);
      const a = hex.length === 4 ? parseInt(expand(hex[3]), 16) / 255 : 1;
      return { r, g, b, a };
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
      return { r, g, b, a };
    }
  }

  // rgb/rgba
  let m = s.match(/^rgba?\(\s*([^)]+)\)$/);
  if (m) {
    const parts = m[1].split(/\s*,\s*/);
    const to255 = (x: string) =>
      x.endsWith('%') ? (parseFloat(x) / 100) * 255 : parseFloat(x);
    const r = clamp(Math.round(to255(parts[0])), 0, 255);
    const g = clamp(Math.round(to255(parts[1])), 0, 255);
    const b = clamp(Math.round(to255(parts[2])), 0, 255);
    const a = parts[3] != null ? clamp(parseFloat(parts[3]), 0, 1) : 1;
    return { r, g, b, a };
  }

  // hsl/hsla
  m = s.match(/^hsla?\(\s*([^)]+)\)$/);
  if (m) {
    const parts = m[1].split(/\s*,\s*/);
    const h = ((parseFloat(parts[0]) % 360) + 360) % 360;
    const sPct = clamp(parseFloat(parts[1]), 0, 100);
    const lPct = clamp(parseFloat(parts[2]), 0, 100);
    const a = parts[3] != null ? clamp(parseFloat(parts[3]), 0, 1) : 1;
    return hslaToRgba({ h, s: sPct, l: lPct, a });
  }

  throw new Error(`Unsupported color format: ${input}`);
}

/** RGB(0–255) -> HSL(0–360, 0–100, 0–100) */
export function rgbaToHsla({
  r,
  g,
  b,
  a = 1,
}: {
  r: number;
  g: number;
  b: number;
  a?: number;
}) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;

  let h = 0,
    s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = 60 * (((g - b) / d) % 6);
        break;
      case g:
        h = 60 * ((b - r) / d + 2);
        break;
      case b:
        h = 60 * ((r - g) / d + 4);
        break;
    }
  }
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100, a };
}

/** HSL(0–360,0–100,0–100) -> RGBA(0–255) */
export function hslaToRgba({
  h,
  s,
  l,
  a = 1,
}: {
  h: number;
  s: number;
  l: number;
  a?: number;
}) {
  const S = clamp(s / 100, 0, 1);
  const L = clamp(l / 100, 0, 1);
  const C = (1 - Math.abs(2 * L - 1)) * S;
  const X = C * (1 - Math.abs(fract(h / 60) * 2 - 1));
  const m = L - C / 2;
  let r1 = 0,
    g1 = 0,
    b1 = 0;

  if (0 <= h && h < 60) {
    r1 = C;
    g1 = X;
    b1 = 0;
  } else if (60 <= h && h < 120) {
    r1 = X;
    g1 = C;
    b1 = 0;
  } else if (120 <= h && h < 180) {
    r1 = 0;
    g1 = C;
    b1 = X;
  } else if (180 <= h && h < 240) {
    r1 = 0;
    g1 = X;
    b1 = C;
  } else if (240 <= h && h < 300) {
    r1 = X;
    g1 = 0;
    b1 = C;
  } else {
    r1 = C;
    g1 = 0;
    b1 = X;
  }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
    a: clamp(a, 0, 1),
  };
}

/** Format RGBA back to css string (hex if opaque, else rgba()) */
export function rgbaToString({
  r,
  g,
  b,
  a = 1,
}: {
  r: number;
  g: number;
  b: number;
  a?: number;
}) {
  if (a >= 0.999) {
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
  return `rgba(${r}, ${g}, ${b}, ${+a.toFixed(3)})`;
}

/** High-level adjuster: pass a color and deltas to get a new color */
export function adjustColor(
  input: string,
  {
    hue = 0, // degrees, positive or negative
    saturate = 0, // +% to add to saturation (e.g. 10 = +10)
    desaturate = 0, // -% to remove (e.g. 20 = -20)
    lighten = 0, // +% to add to lightness
    darken = 0, // -% to remove
    alpha = null, // set alpha 0..1 or null to leave
    mixWith = null, // optional mix target color
    mix = 0, // 0..1 amount to mix toward mixWith
  } = {}
) {
  const base = parseColor(input);
  const baseHsl = rgbaToHsla(base);

  let h = (baseHsl.h + hue) % 360;
  if (h < 0) h += 360;
  const s = clamp(baseHsl.s + saturate - desaturate, 0, 100);
  const l = clamp(baseHsl.l + lighten - darken, 0, 100);
  const a = alpha == null ? baseHsl.a : clamp(alpha, 0, 1);

  let out = hslaToRgba({ h, s, l, a });

  if (mixWith && mix > 0) {
    const tgt = parseColor(mixWith);
    out = {
      r: Math.round(out.r * (1 - mix) + tgt.r * mix),
      g: Math.round(out.g * (1 - mix) + tgt.g * mix),
      b: Math.round(out.b * (1 - mix) + tgt.b * mix),
      a: out.a * (1 - mix) + tgt.a * mix,
    };
  }

  return rgbaToString(out);
}

// Convenience helpers:
export const lightenBy = (c: string, pct: number) =>
  adjustColor(c, { lighten: pct });
export const darkenBy = (c: string, pct: number) =>
  adjustColor(c, { darken: pct });
export const saturateBy = (c: string, pct: number) =>
  adjustColor(c, { saturate: pct });
export const desaturateBy = (c: string, pct: number) =>
  adjustColor(c, { desaturate: pct });
export const setAlpha = (c: string, a: null | undefined) =>
  adjustColor(c, { alpha: a });
